import { MinMaxFilter } from '@appTypes/FilterTypes';
import { ErrorWrapper, Typography } from '@components/style';
import { theme } from '@theme';
import { FC, useRef, useState } from 'react';
import styled from 'styled-components';
import { RenderPosition } from '../../../../enums/RenderPosition';
import { useRenderPosition } from '../../../../hooks/useRenderPosition';
import Calendar from '../../calendar/Calendar';

type CalenderWrapperProps = {
  filterHeight: number;
  maxDate?: Date;
  minDate?: Date;
  hasInvalidRange: boolean;
  fromDate: Date;
  toDate: Date;
  value: MinMaxFilter<number>;
  defaultValue?: MinMaxFilter<number>;
  onDateChange: (date: Date, isStartDate: boolean, value?: MinMaxFilter<number>) => void;
  reset: () => void;
};

const CalenderWrapper: FC<CalenderWrapperProps> = ({
  filterHeight,
  hasInvalidRange,
  fromDate,
  toDate,
  value,
  maxDate,
  minDate,
  defaultValue = { min: null, max: null },
  onDateChange,
  reset,
}) => {
  const calendarOptionsRef = useRef<HTMLElement>(null);
  const renderPosition = useRenderPosition(calendarOptionsRef);

  const [isFromDateOpen, setIsFromDateOpen] = useState<boolean>(false);
  const [isToDateOpen, setIsToDateOpen] = useState<boolean>(false);

  return (
    <Wrapper
      ref={calendarOptionsRef as any}
      className={hasInvalidRange ? 'has-error' : ''}
      renderPosition={renderPosition}
      filterHeight={filterHeight}
    >
      {hasInvalidRange && (
        <ErrorWrapper data-testid='range-error' marginTop={10}>
          Invalid Range
        </ErrorWrapper>
      )}
      <div className={`calender-top ${hasInvalidRange ? 'has-error' : ''}`}>
        <Typography
          size={theme.typography.h5}
          fontWeight={theme.typography.fontWeights.semibold}
          marginLeft={20}
        >
          From
        </Typography>
        <Calendar
          date={fromDate}
          minDate={minDate}
          maxDate={maxDate ?? new Date()}
          isStartDate={true}
          isCalendarOpen={isFromDateOpen}
          isOtherCalendarOpen={isToDateOpen}
          value={value}
          setIsCalendarOpen={setIsFromDateOpen}
          setIsOtherCalendarOpen={setIsToDateOpen}
          onChange={onDateChange}
        />
      </div>
      <div className='calender-bottom'>
        <Typography
          size={theme.typography.h5}
          fontWeight={theme.typography.fontWeights.semibold}
          marginLeft={20}
        >
          To
        </Typography>
        <Calendar
          date={toDate}
          minDate={minDate}
          maxDate={maxDate ?? new Date()}
          isStartDate={false}
          isCalendarOpen={isToDateOpen}
          isOtherCalendarOpen={isFromDateOpen}
          value={value}
          setIsCalendarOpen={setIsToDateOpen}
          setIsOtherCalendarOpen={setIsFromDateOpen}
          onChange={onDateChange}
        />
      </div>
      <div className='reset-link-container'>
        <span
          data-testid='reset-link'
          className={`reset-link ${(value && value.min !== defaultValue?.min) || (value && value.max !== defaultValue?.max) ? '' : 'disabled'}`}
          onClick={reset}
        >
          <a>Reset</a>
        </span>
      </div>
    </Wrapper>
  );
};

export default CalenderWrapper;

type WrapperProps = {
  renderPosition?: RenderPosition;
  filterHeight?: number;
};

const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  min-height: 190px;
  position: absolute;
  background-color: ${theme.palette.white};
  display: flex;
  flex-direction: column;
  border-radius: 3px;
  box-shadow: 0px 2px 5px 3px rgb(0 0 0 / 20%);
  z-index: 9;
  left: 0;

  ${(props) =>
    props.renderPosition === RenderPosition.Top ? `bottom: ${props.filterHeight}px` : ``};
  ${(props) =>
    props.renderPosition === RenderPosition.Bottom ? `top: ${props.filterHeight}px` : ``};

  &.has-error {
    min-height: 215px;
  }

  .calender-top {
    margin-top: 10px;
    &.has-error {
      margin-top: 6px;
    }
  }

  .calender-bottom {
    margin-top: 20px;
  }

  .reset-link-container {
    margin-top: 15px;
  }

  span.reset-link {
    float: right;
    margin-right: 25px;
    a {
      color: ${theme.palette.linkColor} !important;

      &:hover {
        background-color: initial !important;
        color: initial !important;
      }

      &:focus {
        background-color: initial !important;
        color: ${theme.palette.linkColor} !important;
      }

      &.disabled {
        color: ${theme.palette.wetAsphalt} !important;
        cursor: initial;

        &:hover {
          text-decoration: none;
          cursor: initial;
        }
      }
    }

    &.disabled {
      a {
        color: ${theme.palette.wetAsphalt} !important;
        cursor: initial;

        &:hover {
          text-decoration: none;
        }
      }
    }
  }
`;
